import { Button, Form, Input, Spin } from "antd";
import TextArea from "antd/es/input/TextArea";
import { StepProps } from "./StepProps";
import { useTranslation } from "react-i18next";
import { ReactComponent as QuincyIcon } from "shared/assets/images/icon-quincy-pitching.svg";
import { LoadingOutlined } from "@ant-design/icons";
import { ReactComponent as PencilIcon } from "shared/assets/images/pencil-icon.svg";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LeftOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { generatePitchingQuincy } from "shared/service/StrmService";

const removeMarkdown = (text: string) => {
  return text
    .replace(/[#_*~>`]/g, "")
    .replace(/\[.*?\]\(.*?\)/g, "")
    .replace(/!\[.*?\]\(.*?\)/g, "")
    .replace(/`{1,3}.*?`{1,3}/g, "")
    .replace(/\n+/g, " ");
};

enum CurrentAction {
  None,
  Editing,
  Generating,
}

const TextAreaQuincy: React.FC<any> = ({ visible }) => {
  const { t, i18n } = useTranslation();
  const [currentAction, setCurrentAction] = useState<CurrentAction>(
    CurrentAction.Generating
  );
  const [prompt, setPrompt] = useState("");
  const [previousPitching, setPreviousPitching] = useState("");

  const form = Form.useFormInstance();

  const onCancelEdit = () => {
    setCurrentAction(CurrentAction.None);
    form.setFieldValue("pitching", previousPitching);
  };

  const onEdit = () => {
    setCurrentAction(CurrentAction.Editing);
    setPreviousPitching(form.getFieldValue("pitching"));
  };

  const onGenerate = async () => {
    await regenerateQuincy();
  };

  const onKeyDownPrompt = async (ev: any) => {
    if (ev.key === "Enter") {
      ev.preventDefault();
      await regenerateQuincy();
    }
  };

  const onConfirmEdit = async () => {
    setCurrentAction(CurrentAction.None);
  };

  const handleQuincy = async (request: any): Promise<any> => {
    const { response }: any = await generatePitchingQuincy({
      ...request,
      userLoggedLanguage: i18n.language,
    });

    return removeMarkdown(response.pitchingQuincy);
  };

  const regenerateQuincy = async () => {
    if (currentAction === CurrentAction.Generating) return;

    setCurrentAction(CurrentAction.Generating);
    const response = await handleQuincy({
      ...form.getFieldsValue(),
      suggestion: prompt,
      currentPitching: form.getFieldValue("pitching"),
    });
    form.setFieldValue("pitching", response);
    form.setFieldValue("pitchingByQuincy", response);
    setCurrentAction(CurrentAction.None);
  };

  useEffect(() => {
    const quincyGeneratingPitching = async () => {
      setCurrentAction(CurrentAction.Generating);
      const response = await handleQuincy({
        ...form.getFieldsValue(),
      });
      form.setFieldValue("pitching", response);
      form.setFieldValue("pitchingByQuincy", response);
      setCurrentAction(CurrentAction.None);
    };
    if (visible) quincyGeneratingPitching();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <div className="wrapper-quincy">
      <Form.Item name="pitchingByQuincy" hidden>
        <Input />
      </Form.Item>
      <div className="header">
        {currentAction === CurrentAction.Editing ? (
          <button className="btn-cancel" onClick={onCancelEdit}>
            <LeftOutlined />
          </button>
        ) : (
          <>
            <span className="icon">
              <QuincyIcon />
            </span>
            <span className="name">
              {t("pages.Pitching.steps.sixth.quincyIA")}
            </span>
          </>
        )}
      </div>
      <div className="body">
        {currentAction === CurrentAction.Generating && (
          <span className="quincy-loading">
            <Spin
              className="spin-loading"
              indicator={<LoadingOutlined spin />}
            />
            {t("pages.Pitching.steps.sixth.gerandoSeuPitching")}
          </span>
        )}
        <Form.Item
          name="pitching"
          rules={[
            {
              required: true,
            },
          ]}
          hidden={currentAction === CurrentAction.Generating}
        >
          <TextArea
            className={`textarea-quincy ${
              currentAction === CurrentAction.None && "readonly"
            }`}
            rows={12}
            maxLength={500}
            readOnly={currentAction === CurrentAction.None}
          />
        </Form.Item>
        {currentAction !== CurrentAction.Generating && (
          <div className="div-count">
            <div className="counter">
              <span className="green">
                {form.getFieldValue("pitching")?.length ?? 0}
              </span>
              /500
            </div>
            {currentAction === CurrentAction.Editing && (
              <div>
                <Button className="botao-branco" onClick={onConfirmEdit}>
                  {t("pages.Pitching.steps.sixth.confirmar")}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      {currentAction === CurrentAction.None && (
        <div className="footer">
          <div className="buttons">
            <button className="round" onClick={onEdit}>
              <PencilIcon />
            </button>
            <button className="btn-generate-pitching" onClick={onGenerate}>
              <QuincyIcon />
              <span className="">
                {t("pages.Pitching.steps.sixth.gerarNovamente")}
              </span>
            </button>
          </div>
          <div className="div-prompt">
            <Input
              className="prompt-chat"
              placeholder={t("pages.Pitching.steps.sixth.quincyPlaceholder")}
              suffix={
                <button
                  className="btn-prompt-icon"
                  disabled={!prompt}
                  onClick={onGenerate}
                >
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="input-prompt-icon"
                    color="#0D0D0D"
                  />
                </button>
              }
              onChange={(ev) => setPrompt(ev.target.value)}
              onKeyDown={onKeyDownPrompt}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const SixthStep: React.FC<StepProps> = ({ visible }) => {
  const { t } = useTranslation();

  return (
    <div className="sixth-step">
      <div>
        <p className="title">{t("pages.Pitching.steps.sixth.title")}</p>
        <p className="subtitle">{t("pages.Pitching.steps.sixth.subtitle")}</p>
      </div>
      <TextAreaQuincy visible={visible} />
    </div>
  );
};

export default SixthStep;
